/**
 * MeshSolidify — filter that thickens surface meshes by offsetting along normals.
 *
 * Takes the currently visible mesh parts and creates a solidified (closed) version
 * with configurable thickness and direction (outward / inward / both).
 */
class MeshSolidify {
  constructor(viewer, filters) {
    this.viewer = viewer;
    this.filters = filters;
    this.engine = viewer.engine;
    this.active = false;
    this.solidifiedObjectId = 'solidify-filter';
  }

  /**
   * Apply the solidify operation to selected mesh parts.
   * @param {number} thicknessPercent — thickness as % of mesh bbox diagonal
   * @param {string} direction — 'outward' | 'inward' | 'both'
   * @param {Set<string>} [selectedParts] — specific parts to solidify; if null, uses all visible parts
   */
  apply(thicknessPercent, direction, selectedParts) {
    // Remove previous result
    this.remove();

    // Get the active mesh object
    const meshObjectId = this.viewer.meshParts.activeMeshObjectId || 'main-mesh';
    const meshEntry = this.engine.objects.get(meshObjectId)
                   || this.engine.objects.get('main-mesh');
    if (!meshEntry) {
      console.warn('[MeshSolidify] No active mesh object found');
      return { parts: 0, triangles: 0 };
    }

    const rootObj = meshEntry.object;

    // Compute thickness in world units
    const box = new THREE.Box3().setFromObject(rootObj);
    if (box.isEmpty()) return { parts: 0, triangles: 0 };
    const diag = new THREE.Vector3().subVectors(box.max, box.min).length();
    const thickness = (thicknessPercent / 100) * diag;

    // Determine which parts to solidify:
    // If selectedParts is provided (from the part selector UI), use that.
    // Otherwise fall back to all visible parts.
    const partsFilter = selectedParts || this.viewer.meshParts.visibleParts;
    const meshesToSolidify = [];

    if (rootObj.type === 'Group') {
      rootObj.traverse((child) => {
        if (child.isMesh && child.geometry && child.visible) {
          const partName = child.userData.partName || child.name;
          if (!partName || partsFilter.has(partName)) {
            meshesToSolidify.push(child);
          }
        }
      });
    } else if (rootObj.isMesh && rootObj.geometry && rootObj.visible) {
      meshesToSolidify.push(rootObj);
    }

    if (meshesToSolidify.length === 0) {
      console.warn('[MeshSolidify] No visible mesh parts to solidify');
      return { parts: 0, triangles: 0 };
    }

    // Solidify each part
    const resultGroup = new THREE.Group();
    resultGroup.name = 'solidify-filter';
    let totalTriangles = 0;

    for (const srcMesh of meshesToSolidify) {
      const solidGeo = this._solidifyGeometry(srcMesh.geometry, thickness, direction);
      if (!solidGeo) continue;

      // Clone material from the source mesh, stripping any UI highlight
      const mat = srcMesh.material.clone();
      mat.side = THREE.DoubleSide;
      if (srcMesh.userData._origEmissiveHex != null && mat.emissive) {
        mat.emissive.setHex(srcMesh.userData._origEmissiveHex);
      }
      const solidMesh = new THREE.Mesh(solidGeo, mat);

      // Copy part name from source so downloadOBJ can filter by name
      solidMesh.name = srcMesh.userData.partName || srcMesh.name || '';

      // Copy world transform from source
      solidMesh.matrixAutoUpdate = false;
      srcMesh.updateMatrixWorld(true);
      solidMesh.matrix.copy(srcMesh.matrixWorld);
      solidMesh.matrixWorldNeedsUpdate = true;

      resultGroup.add(solidMesh);

      const idxAttr = solidGeo.index;
      totalTriangles += idxAttr ? idxAttr.count / 3 : solidGeo.attributes.position.count / 3;
    }

    // Add to scene
    this.engine.addObject(this.solidifiedObjectId, resultGroup, {
      type: 'filter',
      kind: 'solidify',
      name: 'Mesh Solidify',
      removable: true
    });

    this.active = true;

    if (this.engine.requestRender) this.engine.requestRender();

    return { parts: meshesToSolidify.length, triangles: totalTriangles };
  }

  /**
   * Core solidify algorithm for a single geometry.
   * Creates outer + inner surfaces and side walls along boundary edges.
   */
  _solidifyGeometry(srcGeo, thickness, direction) {
    const posAttr = srcGeo.attributes.position;
    const normAttr = srcGeo.attributes.normal;
    if (!posAttr || !normAttr) return null;

    const vertexCount = posAttr.count;

    // Ensure we have an index buffer
    let srcIndices;
    if (srcGeo.index) {
      srcIndices = srcGeo.index.array;
    } else {
      // Non-indexed: create sequential indices
      srcIndices = new Uint32Array(vertexCount);
      for (let i = 0; i < vertexCount; i++) srcIndices[i] = i;
    }
    const triCount = srcIndices.length / 3;

    // Calculate offsets based on direction
    let outerOffset, innerOffset;
    switch (direction) {
      case 'outward':
        outerOffset = thickness;
        innerOffset = 0;
        break;
      case 'inward':
        outerOffset = 0;
        innerOffset = -thickness;
        break;
      case 'both':
      default:
        outerOffset = thickness / 2;
        innerOffset = -thickness / 2;
        break;
    }

    // Merge normals at coincident vertex positions.
    // Non-indexed and split-vertex geometries (OBJ hard edges, STEP/CAD)
    // duplicate vertices per face, each copy carrying its own face normal.
    // Offsetting each copy along its individual normal pushes them apart,
    // creating gaps. Averaging normals at each position yields a single
    // consistent offset direction that keeps adjacent faces connected.
    const _merged = new Map();
    const _P = 1e6; // quantisation precision
    const _qk = (x, y, z) =>
      `${Math.round(x * _P)},${Math.round(y * _P)},${Math.round(z * _P)}`;

    for (let i = 0; i < vertexCount; i++) {
      const key = _qk(posAttr.getX(i), posAttr.getY(i), posAttr.getZ(i));
      const e = _merged.get(key);
      if (e) {
        e[0] += normAttr.getX(i);
        e[1] += normAttr.getY(i);
        e[2] += normAttr.getZ(i);
      } else {
        _merged.set(key, [normAttr.getX(i), normAttr.getY(i), normAttr.getZ(i)]);
      }
    }
    for (const n of _merged.values()) {
      const len = Math.sqrt(n[0] * n[0] + n[1] * n[1] + n[2] * n[2]);
      if (len > 1e-8) { n[0] /= len; n[1] /= len; n[2] /= len; }
    }

    // Build outer and inner vertex arrays
    // Outer vertices: indices [0 .. vertexCount-1]
    // Inner vertices: indices [vertexCount .. 2*vertexCount-1]
    const totalVerts = vertexCount * 2;
    const positions = new Float32Array(totalVerts * 3);
    const normals = new Float32Array(totalVerts * 3);

    for (let i = 0; i < vertexCount; i++) {
      const px = posAttr.getX(i);
      const py = posAttr.getY(i);
      const pz = posAttr.getZ(i);

      // Merged offset direction (shared across coincident vertices)
      const m = _merged.get(_qk(px, py, pz));
      const ox = m[0], oy = m[1], oz = m[2];

      // Original per-vertex normal (for shading)
      const nx = normAttr.getX(i);
      const ny = normAttr.getY(i);
      const nz = normAttr.getZ(i);

      const i3 = i * 3;
      const j3 = (i + vertexCount) * 3;

      // Outer surface — offset along merged normal
      positions[i3]     = px + ox * outerOffset;
      positions[i3 + 1] = py + oy * outerOffset;
      positions[i3 + 2] = pz + oz * outerOffset;
      normals[i3]     = nx;
      normals[i3 + 1] = ny;
      normals[i3 + 2] = nz;

      // Inner surface — offset along merged normal, normals flipped
      positions[j3]     = px + ox * innerOffset;
      positions[j3 + 1] = py + oy * innerOffset;
      positions[j3 + 2] = pz + oz * innerOffset;
      normals[j3]     = -nx;
      normals[j3 + 1] = -ny;
      normals[j3 + 2] = -nz;
    }

    // Build index arrays for outer and inner surfaces
    // Outer: same winding as original
    // Inner: reversed winding
    const surfaceIndexCount = triCount * 3 * 2; // outer + inner
    const surfaceIndices = [];

    for (let t = 0; t < triCount; t++) {
      const base = t * 3;
      const a = srcIndices[base];
      const b = srcIndices[base + 1];
      const c = srcIndices[base + 2];

      // Outer surface (original winding)
      surfaceIndices.push(a, b, c);

      // Inner surface (reversed winding, offset by vertexCount)
      surfaceIndices.push(
        c + vertexCount,
        b + vertexCount,
        a + vertexCount
      );
    }

    // Boundary edge detection
    // An edge is a boundary if it appears in exactly 1 triangle
    const edgeMap = new Map(); // "minIdx-maxIdx" → { count, outerA, outerB }

    for (let t = 0; t < triCount; t++) {
      const base = t * 3;
      const verts = [srcIndices[base], srcIndices[base + 1], srcIndices[base + 2]];

      for (let e = 0; e < 3; e++) {
        const ea = verts[e];
        const eb = verts[(e + 1) % 3];
        const edgeKey = ea < eb ? `${ea}-${eb}` : `${eb}-${ea}`;

        if (edgeMap.has(edgeKey)) {
          edgeMap.get(edgeKey).count++;
        } else {
          // Store with the winding-ordered vertices for correct side wall orientation
          edgeMap.set(edgeKey, { count: 1, a: ea, b: eb });
        }
      }
    }

    // Build side wall triangles for boundary edges
    const wallIndices = [];
    for (const [, edge] of edgeMap) {
      if (edge.count !== 1) continue;

      // Outer edge: edge.a, edge.b
      // Inner edge: edge.a + vertexCount, edge.b + vertexCount
      // Two triangles forming a quad, winding consistent with the outer normal direction
      const oA = edge.a;
      const oB = edge.b;
      const iA = edge.a + vertexCount;
      const iB = edge.b + vertexCount;

      // Quad: oA → oB → iB → iA
      wallIndices.push(oA, oB, iB);
      wallIndices.push(oA, iB, iA);
    }

    // Combine all indices
    const allIndices = new Uint32Array(surfaceIndices.length + wallIndices.length);
    allIndices.set(surfaceIndices, 0);
    allIndices.set(wallIndices, surfaceIndices.length);

    // Build final geometry
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geo.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
    geo.setIndex(new THREE.BufferAttribute(allIndices, 1));

    // Recompute normals for the side walls (the surface normals were set directly)
    geo.computeVertexNormals();

    return geo;
  }

  /** Remove solidified mesh from the scene */
  remove() {
    if (this.engine.objects.has(this.solidifiedObjectId)) {
      this.engine.removeObject(this.solidifiedObjectId);
    }
    this.active = false;
    if (this.engine.requestRender) this.engine.requestRender();
  }

  /** Return an array of part names from the active mesh object */
  getAvailableParts() {
    const meshObjectId = this.viewer.meshParts.activeMeshObjectId || 'main-mesh';
    const meshEntry = this.engine.objects.get(meshObjectId)
                   || this.engine.objects.get('main-mesh');
    if (!meshEntry) return [];

    const rootObj = meshEntry.object;
    const parts = [];

    if (rootObj.type === 'Group') {
      rootObj.traverse((child) => {
        if (child.isMesh && child.geometry) {
          const partName = child.userData.partName || child.name;
          if (partName) parts.push(partName);
        }
      });
    }

    return parts;
  }

  /** Toggle visibility of the solidified mesh */
  toggle(visible) {
    const entry = this.engine.objects.get(this.solidifiedObjectId);
    if (entry) {
      entry.object.visible = visible;
      if (this.engine.requestRender) this.engine.requestRender();
    }
  }

  /**
   * Export an OBJ file for the currently selected parts.
   * Parts that were solidified use their solidified geometry;
   * parts that were NOT solidified use the original mesh geometry.
   * @param {Set<string>|null} [selectedParts] — parts to export; if null, exports all available parts
   */
  downloadOBJ(selectedParts) {
    // Build a map of solidified part name → solidified mesh child
    const solidifiedByName = new Map();
    const solidEntry = this.engine.objects.get(this.solidifiedObjectId);
    if (solidEntry) {
      solidEntry.object.traverse((child) => {
        if (child.isMesh && child.geometry && child.name) {
          solidifiedByName.set(child.name, child);
        }
      });
    }

    // Get the original mesh object for non-solidified parts
    const meshObjectId = this.viewer.meshParts.activeMeshObjectId || 'main-mesh';
    const meshEntry = this.engine.objects.get(meshObjectId)
                   || this.engine.objects.get('main-mesh');
    if (!meshEntry) {
      console.warn('[MeshSolidify] No mesh object found for export');
      return;
    }

    const rootObj = meshEntry.object;

    // Collect meshes to export: solidified version if available, otherwise original
    const meshes = [];
    const partsToExport = selectedParts || this.viewer.meshParts.visibleParts;

    if (rootObj.type === 'Group') {
      rootObj.traverse((child) => {
        if (!child.isMesh || !child.geometry) return;
        const partName = child.userData.partName || child.name;
        if (!partName || !partsToExport.has(partName)) return;

        // Prefer solidified version, fall back to original
        const solidMesh = solidifiedByName.get(partName);
        meshes.push(solidMesh || child);
      });
    } else if (rootObj.isMesh && rootObj.geometry) {
      meshes.push(solidEntry ? solidEntry.object : rootObj);
    }

    if (meshes.length === 0) return;

    const lines = ['# CoandaView Solidified Mesh Export'];
    let vertexOffset = 1; // OBJ vertex indices are 1-based

    const _v = new THREE.Vector3();
    const _n = new THREE.Vector3();

    for (let m = 0; m < meshes.length; m++) {
      const mesh = meshes[m];
      const geo = mesh.geometry;
      const pos = geo.attributes.position;
      const norm = geo.attributes.normal;
      const idx = geo.index;

      // Get world transform
      mesh.updateMatrixWorld(true);
      const matrixWorld = mesh.matrixWorld;
      const normalMatrix = new THREE.Matrix3().getNormalMatrix(matrixWorld);

      // Determine part name — try to recover from the source mesh name
      const partName = mesh.name || `part_${m + 1}`;
      lines.push(`g ${partName}`);

      // Write vertices
      const vertCount = pos.count;
      for (let i = 0; i < vertCount; i++) {
        _v.fromBufferAttribute(pos, i).applyMatrix4(matrixWorld);
        lines.push(`v ${_v.x} ${_v.y} ${_v.z}`);
      }

      // Write normals
      if (norm) {
        for (let i = 0; i < vertCount; i++) {
          _n.fromBufferAttribute(norm, i).applyMatrix3(normalMatrix).normalize();
          lines.push(`vn ${_n.x} ${_n.y} ${_n.z}`);
        }
      }

      // Write faces
      const triCount = idx ? idx.count / 3 : vertCount / 3;
      for (let t = 0; t < triCount; t++) {
        const i0 = (idx ? idx.getX(t * 3) : t * 3) + vertexOffset;
        const i1 = (idx ? idx.getX(t * 3 + 1) : t * 3 + 1) + vertexOffset;
        const i2 = (idx ? idx.getX(t * 3 + 2) : t * 3 + 2) + vertexOffset;

        if (norm) {
          lines.push(`f ${i0}//${i0} ${i1}//${i1} ${i2}//${i2}`);
        } else {
          lines.push(`f ${i0} ${i1} ${i2}`);
        }
      }

      vertexOffset += vertCount;
    }

    // Trigger download
    const content = lines.join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'solidified-mesh.obj';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}

window.MeshSolidify = MeshSolidify;
